/**
 * Shared auth-screen controls.
 *
 * PasswordField — masked input with a show/hide toggle and a Caps-Lock hint.
 * Both are near-universal conventions (Jakob's Law) and matter doubly for the
 * older audience: masked fields are the #1 silent cause of "wrong password".
 *
 * AuthSubmit — the single brass primary action, with a buttery busy-state
 * (in-button spinner + busy label) and an optional success check (peak-end).
 * The accessible name is preserved per state (idle label / busy label) so the
 * existing route tests and screen readers see the right text.
 *
 * Styling + motion live in ./auth-controls.css (transform/opacity only,
 * disabled under prefers-reduced-motion). Tokens from design-system.css.
 */

import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Eye, EyeOff, Check } from 'lucide-react';
import { PasswordRequirements } from './password-requirements';
import './auth-controls.css';

interface PasswordFieldProps {
  id: string;
  /** Visible label text — keep it exactly "Password" where a test asserts it. */
  label: string;
  /**
   * Optional one-line hint rendered under the label. A field where a NEW
   * password is chosen should use `requirementsId` instead: the rules are four,
   * not one, and a flat sentence cannot show which of them you have reached.
   */
  hint?: string;
  /**
   * Pass an id to draw the live password-rule checklist under this field
   * (SGN-02, 2026-08-27). Only a field where a NEW password is being chosen
   * wants it: sign-in and confirm boxes leave it off.
   */
  requirementsId?: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  /** 'current-password' on sign-in, 'new-password' on sign-up / reset. */
  autoComplete: string;
  ariaInvalid: boolean;
  ariaDescribedby?: string;
  disabled?: boolean;
  minLength?: number;
  required?: boolean;
}

export function PasswordField({
  id,
  label,
  hint,
  requirementsId,
  placeholder,
  value,
  onChange,
  autoComplete,
  ariaInvalid,
  ariaDescribedby,
  disabled = false,
  minLength,
  required = true,
}: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [focused, setFocused] = useState(false);
  const describers = [requirementsId, ariaDescribedby].filter(Boolean).join(' ');

  function readCaps(e: KeyboardEvent<HTMLInputElement>) {
    if (typeof e.getModifierState === 'function') {
      setCapsOn(e.getModifierState('CapsLock'));
    }
  }

  return (
    <div className="auth-field">
      <label htmlFor={id} className="auth-label">
        {label}
        {hint && <span className="auth-hint">{hint}</span>}
      </label>
      <div className="auth-pw-wrap">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          className="auth-input auth-pw-input"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyUp={readCaps}
          onKeyDown={readCaps}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          autoComplete={autoComplete}
          aria-invalid={ariaInvalid}
          aria-describedby={describers.length > 0 ? describers : undefined}
          disabled={disabled}
          minLength={minLength}
          required={required}
        />
        <button
          type="button"
          className="auth-pw-toggle"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          tabIndex={disabled ? -1 : 0}
        >
          {visible ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
        </button>
      </div>
      {requirementsId !== undefined && (
        <PasswordRequirements value={value} id={requirementsId} />
      )}
      {capsOn && focused && (
        <div className="auth-caps" role="status">
          Caps Lock is on
        </div>
      )}
    </div>
  );
}

interface AuthSubmitProps {
  /** Idle label, e.g. "Sign in" — also the accessible name when idle. */
  label: string;
  /** In-flight label, e.g. "Signing in…" — the accessible name while busy. */
  busyLabel: string;
  busy: boolean;
  /** Brief success state shown before a navigation (peak-end). */
  done?: boolean;
  /** Not-ready state (e.g. MFA before six digits) — muted + non-submitting. */
  disabled?: boolean;
  /** Defaults to a form submit; pass 'button' for onClick-driven screens. */
  type?: 'submit' | 'button';
  onClick?: () => void;
  /**
   * Size-to-content variant (W2, Paul walk 2026-07-13). The default is a
   * full-width bar (the narrow AuthLayout card). Onboarding-wizard CTAs sit in
   * a wider surface where a full-width button reads as oversized, so they opt
   * into a content-width button (sensible min-width, generous side padding);
   * the caller centres it.
   */
  fitContent?: boolean;
  /** Optional `data-testid` — the onboarding wizard's flows key off stable ids. */
  testId?: string;
}

export function AuthSubmit({
  label,
  busyLabel,
  busy,
  done = false,
  disabled = false,
  type = 'submit',
  onClick,
  fitContent = false,
  testId,
}: AuthSubmitProps) {
  const muted = disabled && !busy && !done;
  const classes = ['auth-submit'];
  if (fitContent) classes.push('auth-submit--fit');
  if (busy) classes.push('is-busy');
  if (done) classes.push('is-done');
  if (muted) classes.push('is-muted');

  return (
    <button
      type={type}
      onClick={onClick}
      className={classes.join(' ')}
      disabled={busy || done || disabled}
      {...(testId ? { 'data-testid': testId } : {})}
    >
      {done ? (
        <>
          <Check className="auth-check" aria-hidden="true" />
          <span className="sr-only">{label}: done</span>
        </>
      ) : busy ? (
        <>
          <span className="auth-spinner" aria-hidden="true" />
          <span>{busyLabel}</span>
        </>
      ) : (
        <span>{label}</span>
      )}
    </button>
  );
}
